import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash,
  PencilSimple,
  ListChecks,
  ChartBar,
  Lock,
  X,
  Clock,
  CaretDown,
  FileText,
  ClipboardText,
  Question,
  ChatsCircle,
  CheckCircle,
  PaperPlaneTilt,
  Eye,
  VideoCamera,
  UsersThree,
  CaretLeft,
  CaretRight,
  ListNumbers,
} from "@phosphor-icons/react";
import api, { apiError, coverSrc, lessonVideoUrl, lessonSubtitleUrl, BACKEND_ORIGIN } from "@/lib/api";
import RichEditor from "@/components/rich/RichEditor";
import MarkdownContent from "@/components/MarkdownContent";
import EmptyState from "@/components/EmptyState";
import { LessonHighlights, LessonNote } from "@/components/Highlighter";
import { LessonCheck, CheckEditor } from "@/components/LessonCheck";
import CopyGuard from "@/components/CopyGuard";
import QuizResults from "@/components/QuizResults";
import { ReadingProgressBar, SequenceToc, readingMinutes, readingRatio } from "@/components/ReadingAids";
import CourseMonogram from "@/components/CourseMonogram";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { LiveSessionsPanel } from "@/components/LiveClass";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";

export default function CourseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [sp, setSp] = useSearchParams();
  const tab = ["contenu", "devoirs", "quiz", "forum", "visio"].includes(sp.get("tab")) ? sp.get("tab") : "contenu";
  const changeTab = (v) => { const n = new URLSearchParams(sp); if (v === "contenu") n.delete("tab"); else n.set("tab", v); n.delete("sequence"); setSp(n, { replace: true }); };

  const loadCourse = () => api.get(`/courses/${id}`).then((r) => setCourse(r.data)).catch(() => navigate("/app/cours"));
  useEffect(() => { loadCourse(); }, [id]);

  if (!course) return <div className="text-muted-foreground">Chargement...</div>;
  const canEdit = course.can_edit;
  const isStudent = user.role === "etudiant";

  return (
    <div className="space-y-6" data-testid="course-detail">
      <button onClick={() => navigate("/app/cours")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={16} /> Retour aux cours
      </button>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="relative h-44">
          {coverSrc(course.cover_image) ? (
            <img src={coverSrc(course.cover_image)} alt={course.title} className="h-full w-full object-cover" />
          ) : (
            <CourseMonogram code={course.code} title={course.title} className="h-full w-full" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute bottom-4 left-6 text-white">
            {course.code && <p className="text-xs font-semibold uppercase tracking-wider">{course.code}</p>}
            <h1 className="font-display text-2xl font-extrabold sm:text-3xl">{course.title}</h1>
            <p className="text-sm opacity-90">{course.teacher_name} · {course.students_count} étudiants</p>
          </div>
          {canEdit && (
            <div className="absolute right-4 top-4 flex gap-2">
              <Button size="sm" variant={course.published ? "secondary" : "default"} className={course.published ? "" : "bg-emerald-600 hover:bg-emerald-700"}
                onClick={async () => { await api.post(`/courses/${id}/publish`); toast.success(course.published ? "Dépublié" : "Publié"); loadCourse(); }}
                data-testid="publish-btn">
                {course.published ? "Dépublier" : "Publier"}
              </Button>
            </div>
          )}
        </div>
        <div className="p-6">
          <p className="text-sm leading-relaxed text-muted-foreground">{course.description}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {course.meeting_url && (
              <a href={course.meeting_url} target="_blank" rel="noreferrer">
                <Button className="bg-accent hover:bg-accent/90" data-testid="join-meeting"><VideoCamera size={16} className="mr-2" weight="fill" /> Rejoindre la salle</Button>
              </a>
            )}
            {canEdit && <EditCourse course={course} onSaved={loadCourse} />}
            {canEdit && <CourseSettings course={course} onSaved={loadCourse} />}
            {canEdit && <DeleteCourse course={course} />}
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="contenu" data-testid="tab-contenu"><FileText size={16} className="mr-1" /> Contenu</TabsTrigger>
          <TabsTrigger value="devoirs" data-testid="tab-devoirs"><ClipboardText size={16} className="mr-1" /> Devoirs</TabsTrigger>
          <TabsTrigger value="quiz" data-testid="tab-quiz"><Question size={16} className="mr-1" /> Quiz</TabsTrigger>
          <TabsTrigger value="forum" data-testid="tab-forum"><ChatsCircle size={16} className="mr-1" /> Forum</TabsTrigger>
          <TabsTrigger value="visio" data-testid="tab-visio"><VideoCamera size={16} className="mr-1" /> Visio</TabsTrigger>
        </TabsList>

        <TabsContent value="contenu"><Lessons courseId={id} canEdit={canEdit} isStudent={isStudent} /></TabsContent>
        <TabsContent value="devoirs"><Assignments courseId={id} canEdit={canEdit} isStudent={isStudent} /></TabsContent>
        <TabsContent value="quiz"><Quizzes courseId={id} canEdit={canEdit} isStudent={isStudent} /></TabsContent>
        <TabsContent value="forum"><Forum courseId={id} /></TabsContent>
        <TabsContent value="visio"><LiveSessionsPanel courseId={id} canEdit={canEdit} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- LESSONS ---------------- */
function Lessons({ courseId, canEdit, isStudent }) {
  const [lessons, setLessons] = useState([]);
  const [sections, setSections] = useState([]);
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", type: "text", video_url: "", order: 0, section_id: "", available_from: "", require_previous: false });
  const [editId, setEditId] = useState(null);
  const [doneIds, setDoneIds] = useState([]);
  const [percent, setPercent] = useState(0);
  const [sectionOpen, setSectionOpen] = useState(false);
  const [sectionTitle, setSectionTitle] = useState("");
  const [editSectionId, setEditSectionId] = useState(null);
  const [checkFor, setCheckFor] = useState(null);

  const load = () => api.get(`/courses/${courseId}/lessons`).then((r) => {
    setLessons(r.data);
    setActive((a) => Math.min(a, Math.max(0, r.data.length - 1)));
  });
  const loadSections = () => api.get(`/courses/${courseId}/sections`).then((r) => setSections(r.data));
  const loadProgress = () => {
    if (!isStudent) return;
    api.get(`/courses/${courseId}/progress`).then((r) => { setDoneIds(r.data.completed_lesson_ids); setPercent(r.data.percent); }).catch(() => {});
  };
  useEffect(() => { load(); loadProgress(); loadSections(); }, [courseId]);

  const [editingLesson, setEditingLesson] = useState(null);
  const resetForm = () => { setForm({ title: "", content: "", type: "text", video_url: "", order: 0, section_id: "", available_from: "", require_previous: false }); setEditId(null); setEditingLesson(null); };
  const startEdit = (l) => { setForm({ title: l.title, content: l.content || "", type: l.type || "text", video_url: l.video_url || "", order: l.order, section_id: l.section_id || "", available_from: l.available_from || "", require_previous: !!l.require_previous }); setEditId(l.id); setEditingLesson(l); setOpen(true); };
  const save = async () => {
    const payload = { ...form, section_id: form.section_id || null, available_from: form.available_from || null };
    try {
      if (editId) { await api.put(`/lessons/${editId}`, payload); toast.success("Séquence modifiée"); }
      else { await api.post(`/courses/${courseId}/lessons`, { ...payload, order: lessons.length + 1 }); toast.success("Séquence ajoutée"); }
      setOpen(false); resetForm(); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { await api.delete(`/lessons/${id}`); toast.success("Séquence supprimée"); load(); loadProgress(); };

  const [videoUploading, setVideoUploading] = useState(false);
  const uploadContentImage = async (lessonId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post(`/lessons/${lessonId}/content-image`, fd);
    return `${BACKEND_ORIGIN}${data.url}`;
  };
  const uploadVideo = async (lessonId, file) => {
    if (!file) return;
    setVideoUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/lessons/${lessonId}/video`, fd);
      toast.success("Vidéo envoyée");
      setEditingLesson((prev) => (prev ? { ...prev, video_path: "uploaded", video_filename: file.name } : prev));
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setVideoUploading(false); }
  };
  const deleteVideo = async (lessonId) => {
    if (!window.confirm("Supprimer la vidéo de cette séquence ?")) return;
    try {
      await api.delete(`/lessons/${lessonId}/video`);
      toast.success("Vidéo supprimée");
      setEditingLesson((prev) => (prev ? { ...prev, video_path: null, video_filename: null } : prev));
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const uploadSubtitle = async (lessonId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/lessons/${lessonId}/subtitle`, fd);
      toast.success("Sous-titres ajoutés");
      setEditingLesson((prev) => (prev ? { ...prev, subtitle_path: "uploaded" } : prev));
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const deleteSubtitle = async (lessonId) => {
    try {
      await api.delete(`/lessons/${lessonId}/subtitle`);
      toast.success("Sous-titres supprimés");
      setEditingLesson((prev) => (prev ? { ...prev, subtitle_path: null } : prev));
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggleDone = async (lesson) => {
    const completed = !doneIds.includes(lesson.id);
    try {
      await api.post(`/lessons/${lesson.id}/complete`, { completed, seconds_spent: secondsOnPage, scrolled_to_bottom: scrolledToBottom });
      const { data } = await api.get(`/courses/${courseId}/progress`);
      setDoneIds(data.completed_lesson_ids); setPercent(data.percent);
      load(); // la séquence suivante peut se (dé)verrouiller
      if (completed) toast.success("Séquence marquée comme terminée");
    } catch (e) { toast.error(apiError(e)); }
  };

  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const uploadAttachment = async (lessonId, file) => {
    if (!file) return;
    setUploadingAttachment(true);
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/lessons/${lessonId}/attachments`, fd); toast.success("Pièce jointe ajoutée"); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setUploadingAttachment(false); }
  };
  const downloadAttachment = async (lessonId, att) => {
    try {
      const res = await api.get(`/lessons/${lessonId}/attachments/${att.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = att.filename;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  const deleteAttachment = async (lessonId, attId) => {
    try { await api.delete(`/lessons/${lessonId}/attachments/${attId}`); toast.success("Pièce jointe supprimée"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const openAddSection = () => { setSectionTitle(""); setEditSectionId(null); setSectionOpen(true); };
  const openEditSection = (s) => { setSectionTitle(s.title); setEditSectionId(s.id); setSectionOpen(true); };
  const saveSection = async () => {
    if (!sectionTitle.trim()) return;
    try {
      if (editSectionId) {
        const s = sections.find((x) => x.id === editSectionId);
        await api.put(`/sections/${editSectionId}`, { title: sectionTitle, order: s?.order || 0 });
      } else {
        await api.post(`/courses/${courseId}/sections`, { title: sectionTitle, order: sections.length + 1 });
      }
      setSectionOpen(false); loadSections();
    } catch (e) { toast.error(apiError(e)); }
  };
  const delSection = async (id) => {
    if (!window.confirm("Supprimer ce module ? Les séquences qu'il contient seront conservées, juste détachées du module.")) return;
    try { await api.delete(`/sections/${id}`); toast.success("Module supprimé"); loadSections(); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const grouped = useMemo(() => {
    const bySection = {};
    const noSection = [];
    lessons.forEach((l) => {
      if (l.section_id) { (bySection[l.section_id] = bySection[l.section_id] || []).push(l); }
      else noSection.push(l);
    });
    const groups = sections.map((s) => ({ section: s, items: bySection[s.id] || [] }));
    if (noSection.length > 0) groups.push({ section: null, items: noSection });
    return groups;
  }, [lessons, sections]);

  const current = lessons[active];
  const currentDone = current && doneIds.includes(current.id);

  // Suivi du temps de lecture et du défilement jusqu'en bas du contenu,
  // pour éviter qu'un chapitre soit marqué "terminé" en un clic instantané.
  const [secondsOnPage, setSecondsOnPage] = useState(0);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const contentEndRef = useRef(null);

  useEffect(() => {
    setSecondsOnPage(0);
    setScrolledToBottom(false);
    const timer = setInterval(() => setSecondsOnPage((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [current?.id]);

  useEffect(() => {
    if (!contentEndRef.current) return;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setScrolledToBottom(true); }, { threshold: 0.1 });
    observer.observe(contentEndRef.current);
    return () => observer.disconnect();
  }, [current?.id, current?.locked]);

  const requiredSeconds = current?.required_reading_seconds || 0;
  const readingSatisfied = requiredSeconds === 0 || (secondsOnPage >= requiredSeconds && scrolledToBottom);

  // --- Lecture : haut de page à chaque changement de séquence, reprise, mémorisation de la position ---
  const articleRef = useRef(null);
  const contentCardRef = useRef(null);
  const prevLessonId = useRef(null);
  const [navOpen, setNavOpen] = useState(false);
  const resumeRef = useRef(null);   // { id, ratio } : position à restaurer à l'ouverture
  const resumedOnce = useRef(false);
  const lastSentRatio = useRef(0);

  // Feuille « Sommaire » (téléphone) : fermeture à Échap, page figée derrière, séquence active centrée.
  const activeItemRef = useRef(null);
  useEffect(() => {
    if (!navOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setNavOpen(false); };
    document.addEventListener("keydown", onKey);
    const t = setTimeout(() => activeItemRef.current?.scrollIntoView({ block: "center" }), 60);
    return () => { document.body.style.overflow = prevOverflow; document.removeEventListener("keydown", onKey); clearTimeout(t); };
  }, [navOpen]);

  const goTo = (idx) => setActive(Math.max(0, Math.min(lessons.length - 1, idx)));

  // Reprise automatique (étudiant) : dernière séquence lue de ce cours, au bon endroit.
  useEffect(() => {
    if (!isStudent || lessons.length === 0 || resumedOnce.current) return;
    resumedOnce.current = true;
    const wanted = new URLSearchParams(window.location.search).get("sequence");
    if (wanted) {
      const wi = lessons.findIndex((l) => l.id === wanted);
      if (wi >= 0) { setActive(wi); return; }
    }
    api.get(`/courses/${courseId}/resume`).then((r) => {
      const d = r.data;
      if (!d || !d.lesson_id) return;
      let idx = lessons.findIndex((l) => l.id === d.lesson_id);
      if (idx === -1) return;
      let ratio = d.scroll_ratio || 0;
      if (ratio >= 0.95 && idx < lessons.length - 1) { idx += 1; ratio = 0; }   // séquence finie -> la suivante
      if (idx === 0 && ratio < 0.03) return;
      resumeRef.current = ratio > 0.03 ? { id: lessons[idx].id, ratio } : null;
      setActive(idx);
      toast.info(ratio > 0.03 ? "Reprise là où vous vous étiez arrêté" : "Reprise à votre dernière séquence");
    }).catch(() => {});
  }, [lessons, isStudent, courseId]);

  // À chaque changement de séquence : restaurer la position (reprise) ou revenir en haut de page.
  useEffect(() => {
    if (!current) return undefined;
    lastSentRatio.current = 0;
    const r = resumeRef.current;
    if (r && r.id === current.id) {
      resumeRef.current = null;
      const t = setTimeout(() => {
        const el = articleRef.current;
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.scrollY + r.ratio * el.offsetHeight - window.innerHeight * 0.3;
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      }, 450);
      return () => clearTimeout(t);
    }
    // Première ouverture : on ne bouge pas. Ensuite : on amène le début de la séquence à l'écran
    // (sur téléphone le menu est au-dessus du contenu : remonter tout en haut obligerait à redescendre).
    if (prevLessonId.current !== null) {
      contentCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    return undefined;
  }, [current?.id]);
  useEffect(() => { if (current) prevLessonId.current = current.id; }, [current?.id]);

  // Mémorise la séquence ouverte et la position de lecture (étudiant) pour la reprise
  // et le bloc « Reprendre où vous en étiez » du tableau de bord.
  useEffect(() => {
    if (!isStudent || !current || current.locked) return undefined;
    const lessonId = current.id;
    const send = (ratio) => api.post(`/lessons/${lessonId}/view`, { scroll_ratio: Math.round(ratio * 100) / 100 }).catch(() => {});
    send(0);
    const timer = setInterval(() => {
      const ratio = readingRatio(articleRef.current);
      if (Math.abs(ratio - lastSentRatio.current) >= 0.03) { lastSentRatio.current = ratio; send(ratio); }
    }, 4000);
    return () => {
      clearInterval(timer);
      const ratio = readingRatio(articleRef.current);
      if (Math.abs(ratio - lastSentRatio.current) >= 0.03) send(ratio);
    };
  }, [current?.id, current?.locked, isStudent]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {canEdit && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild><Button variant="outline" data-testid="add-lesson-btn"><Plus size={16} className="mr-2" /> Ajouter une séquence</Button></DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader><DialogTitle>{editId ? "Modifier la séquence" : "Nouvelle séquence"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="lesson-title" /></div>
                <div>
                  <Label>Module (optionnel)</Label>
                  <Select value={form.section_id || "__none"} onValueChange={(v) => setForm({ ...form, section_id: v === "__none" ? "" : v })}>
                    <SelectTrigger data-testid="lesson-section"><SelectValue placeholder="Aucun module" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">Aucun module</SelectItem>
                      {sections.map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Contenu</Label>
                  <RichEditor
                    value={form.content}
                    onChange={(v) => setForm((f) => ({ ...f, content: v }))}
                    onUploadImage={editId ? (file) => uploadContentImage(editId, file) : undefined}
                    placeholder="Écrivez le contenu de la séquence ici..."
                    testIdPrefix="lesson-content"
                    showTemplates={!editId}
                  />
                  {!editId && <p className="mt-1 text-xs text-muted-foreground">L'insertion d'image sera possible après la création de la séquence (enregistrez d'abord, puis modifiez-la).</p>}
                </div>
                <div><Label>Lien vidéo externe (optionnel)</Label><Input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://..." /></div>
                {editId && (
                  <div className="rounded-lg border border-border p-3 space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Vidéo hébergée (optionnel, remplace le lien externe si présente)</p>
                    {editingLesson?.video_path ? (
                      <div className="flex items-center justify-between rounded-lg bg-secondary px-3 py-2 text-sm">
                        <span className="truncate">{editingLesson.video_filename}</span>
                        <Button size="sm" variant="ghost" onClick={() => deleteVideo(editId)} data-testid="delete-lesson-video"><Trash size={14} className="text-accent" /></Button>
                      </div>
                    ) : (
                      <>
                        <Input type="file" accept=".mp4,.webm" disabled={videoUploading} onChange={(e) => { if (e.target.files[0]) uploadVideo(editId, e.target.files[0]); e.target.value = ""; }} data-testid="upload-lesson-video" />
                        {videoUploading && <p className="text-xs text-muted-foreground">Envoi de la vidéo en cours...</p>}
                        <p className="text-xs text-muted-foreground">Formats MP4/WEBM, 500 Mo maximum — attention à l'espace disque du serveur.</p>
                      </>
                    )}
                    {editingLesson?.video_path && (
                      <div className="mt-2">
                        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sous-titres (.vtt, optionnel)</p>
                        {editingLesson.subtitle_path ? (
                          <div className="flex items-center justify-between rounded-lg bg-secondary px-3 py-2 text-sm">
                            <span>Sous-titres ajoutés</span>
                            <Button size="sm" variant="ghost" onClick={() => deleteSubtitle(editId)}><Trash size={14} className="text-accent" /></Button>
                          </div>
                        ) : (
                          <Input type="file" accept=".vtt" onChange={(e) => { if (e.target.files[0]) uploadSubtitle(editId, e.target.files[0]); e.target.value = ""; }} data-testid="upload-lesson-subtitle" />
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div className="rounded-lg border border-border p-3 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Restrictions d'accès (optionnel)</p>
                  <div>
                    <Label>Disponible à partir du</Label>
                    <Input type="date" value={form.available_from} onChange={(e) => setForm({ ...form, available_from: e.target.value })} data-testid="lesson-available-from" />
                  </div>
                  <label className="flex cursor-pointer items-start gap-2 text-sm">
                    <Checkbox checked={form.require_previous} onCheckedChange={(v) => setForm({ ...form, require_previous: !!v })} data-testid="lesson-require-previous" />
                    <span>
                      <span className="font-medium">Verrouillée tant que la séquence précédente n'est pas terminée</span>
                    </span>
                  </label>
                </div>
              </div>
              <DialogFooter><Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="save-lesson-btn">{editId ? "Enregistrer" : "Ajouter"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        {canEdit && (
          <Dialog open={sectionOpen} onOpenChange={setSectionOpen}>
            <DialogTrigger asChild><Button variant="outline" onClick={openAddSection} data-testid="add-section-btn"><Plus size={16} className="mr-2" /> Nouveau module</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editSectionId ? "Renommer le module" : "Nouveau module"}</DialogTitle></DialogHeader>
              <div><Label>Titre</Label><Input value={sectionTitle} onChange={(e) => setSectionTitle(e.target.value)} placeholder="Module 1 : Vision stratégique..." data-testid="section-title" /></div>
              <DialogFooter><Button onClick={saveSection} className="bg-primary hover:bg-primary/90" data-testid="save-section-btn">{editSectionId ? "Enregistrer" : "Créer"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {isStudent && lessons.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4" data-testid="progress-bar">
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="font-medium">Ma progression</span>
            <span className="font-display font-bold text-primary" data-testid="progress-percent">{percent}%</span>
          </div>
          <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-secondary">
            <div className="absolute inset-0 flex" style={{ clipPath: `inset(0 ${100 - percent}% 0 0)` }}>
              <span className="h-full flex-1" style={{ background: "hsl(var(--campus-orange))" }} />
              <span className="h-full flex-1 bg-white" />
              <span className="h-full flex-1" style={{ background: "hsl(var(--campus-green))" }} />
            </div>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {doneIds.length} / {lessons.length} séquences terminées
            {percent === 100 && " · Cours terminé 🎉"}
          </p>
        </div>
      )}

      {lessons.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="Aucune séquence publiée"
          description={canEdit ? "Ajoutez la première séquence pour que les étudiants puissent commencer à apprendre." : "Le contenu de ce cours n'est pas encore disponible."}
          actionLabel={canEdit ? "Ajouter une séquence" : undefined}
          onAction={canEdit ? () => setOpen(true) : undefined}
        />
      ) : (
        <>
        {!navOpen && (
          <button type="button" onClick={() => setNavOpen(true)} aria-label="Ouvrir le sommaire du cours" data-testid="toc-fab"
            className="fixed bottom-4 right-4 z-40 flex h-12 items-center gap-2 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-lg active:scale-95 lg:hidden"
            style={{ marginBottom: "env(safe-area-inset-bottom)" }}>
            <ListNumbers size={20} weight="bold" /> {active + 1}/{lessons.length}
          </button>
        )}
        {navOpen && (
          <div className="fixed inset-0 z-[80] lg:hidden" role="dialog" aria-modal="true" aria-label="Sommaire du cours" data-testid="toc-sheet">
            <div className="absolute inset-0 bg-black/50" onClick={() => setNavOpen(false)} />
            <div className="absolute inset-x-0 bottom-0 flex max-h-[85vh] flex-col rounded-t-2xl border-t border-border bg-card shadow-2xl" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
              <div className="px-4 pb-2 pt-3">
                <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-border" aria-hidden="true" />
                <div className="flex items-center justify-between">
                  <p className="font-display text-base font-bold">Sommaire du cours</p>
                  <button type="button" onClick={() => setNavOpen(false)} aria-label="Fermer" className="rounded-full p-1.5 text-muted-foreground hover:bg-secondary"><X size={20} /></button>
                </div>
                {isStudent && (
                  <div className="mt-2" data-testid="toc-progress">
                    <div className="h-2 w-full overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full bg-primary transition-all" style={{ width: `${percent}%` }} /></div>
                    <p className="mt-1 text-xs text-muted-foreground">{doneIds.length} / {lessons.length} séquences terminées</p>
                  </div>
                )}
              </div>
              <div className="overflow-y-auto px-3 pb-4">
                {grouped.map((group) => (
                  <div key={group.section?.id || "sans-module"} className="mt-3">
                    {sections.length > 0 && <p className="px-2 pb-1 text-xs font-bold uppercase tracking-wide text-foreground">{group.section?.title || "Sans module"}</p>}
                    <div className="space-y-1">
                      {group.items.map((l) => {
                        const i = lessons.findIndex((x) => x.id === l.id);
                        const isDone = doneIds.includes(l.id);
                        const mins = readingMinutes(l.content);
                        return (
                          <button key={l.id} type="button" ref={i === active ? activeItemRef : null}
                            onClick={() => { setActive(i); setNavOpen(false); }} data-testid={`toc-link-${i}`}
                            className={`flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm ${i === active ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>
                            <span className="flex h-6 w-6 shrink-0 items-center justify-center">
                              {isDone ? <CheckCircle size={20} weight="fill" className={i === active ? "" : "text-emerald-600"} />
                                : l.locked ? <Lock size={18} weight="fill" className={i === active ? "" : "text-accent"} />
                                : <span className={`text-xs font-semibold ${i === active ? "" : "text-muted-foreground"}`}>{i + 1}</span>}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="line-clamp-2">{l.title.replace(/^(Chapitre|Séquence)\s*\d+\s*:\s*/i, "")}</span>
                            </span>
                            {mins > 0 && <span className={`shrink-0 text-xs ${i === active ? "opacity-80" : "text-muted-foreground"}`}>{mins} min</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-[280px_minmax(0,1fr)]" data-testid="chapter-layout">
          {/* Chapter navigation menu */}
          <aside className="h-fit min-w-0 rounded-xl lg:sticky lg:top-24 border border-border bg-card p-3" data-testid="chapter-nav">
            <p className="hidden items-center gap-2 px-2 pb-2 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground lg:flex">
              <ListNumbers size={16} /> Modules et séquences
            </p>
            <button type="button" onClick={() => setNavOpen(true)} aria-haspopup="dialog"
              className="flex w-full items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-left lg:hidden" data-testid="chapter-nav-toggle">
              <span className="min-w-0">
                <span className="block truncate text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {sections.find((x) => x.id === current?.section_id)?.title || "Modules et séquences"}
                </span>
                <span className="block truncate text-sm font-medium">
                  {current ? `Séquence ${active + 1}/${lessons.length} · ${current.title.replace(/^(Chapitre|Séquence)\s*\d+\s*:\s*/i, "")}` : ""}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-primary">
                Sommaire <CaretDown size={14} />
              </span>
            </button>
            <nav className="hidden space-y-3 lg:block">
              {grouped.map((group, gi) => (
                <div key={group.section?.id || "sans-section"}>
                  {sections.length > 0 && (
                    <div className="group mb-1 flex items-center justify-between px-2">
                      <p className="text-xs font-bold uppercase tracking-wide text-foreground">{group.section?.title || "Sans module"}</p>
                      {canEdit && group.section && (
                        <div className="hidden gap-0.5 group-hover:flex">
                          <button onClick={() => openEditSection(group.section)} className="text-muted-foreground hover:text-primary"><PencilSimple size={12} /></button>
                          <button onClick={() => delSection(group.section.id)} className="text-muted-foreground hover:text-accent"><Trash size={12} /></button>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="space-y-1">
                    {group.items.map((l) => {
                      const i = lessons.findIndex((x) => x.id === l.id);
                      const isDone = doneIds.includes(l.id);
                      return (
                        <button
                          key={l.id}
                          onClick={() => { setActive(i); setNavOpen(false); }}
                          data-testid={`chapter-link-${i}`}
                          className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                            i === active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                          } ${l.locked ? "opacity-60" : ""}`}
                        >
                          {l.locked ? (
                            <Lock size={22} weight="fill" className="shrink-0 text-accent" />
                          ) : isStudent && isDone ? (
                            <CheckCircle size={22} weight="fill" className={`shrink-0 ${i === active ? "text-primary-foreground" : "text-emerald-600"}`} />
                          ) : (
                            <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${i === active ? "bg-primary-foreground text-primary" : "bg-secondary text-foreground"}`}>{i + 1}</span>
                          )}
                          <span className="line-clamp-2">{l.title.replace(/^(Chapitre|Séquence)\s*\d+\s*:\s*/i, "")}</span>
                        </button>
                      );
                    })}
                    {group.items.length === 0 && <p className="px-3 py-1 text-xs text-muted-foreground">Aucune séquence dans ce module.</p>}
                  </div>
                </div>
              ))}
            </nav>
          </aside>

          {/* Chapter content */}
          {current && (
            <div ref={contentCardRef} className="min-w-0 scroll-mt-20 rounded-xl border border-border bg-card p-4 sm:p-6 lg:scroll-mt-24 lg:p-8" data-testid="chapter-content">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">
                    {sections.find((s) => s.id === current.section_id)?.title ? `${sections.find((s) => s.id === current.section_id).title} · ` : ""}
                    Séquence {active + 1} / {lessons.length}
                  </p>
                  <h2 className="mt-1 font-display text-2xl font-extrabold tracking-tight">{current.title}</h2>
                  {!current.locked && readingMinutes(current.content) > 0 && (
                    <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground" data-testid="reading-time">
                      <Clock size={13} /> ≈ {readingMinutes(current.content)} min de lecture
                    </p>
                  )}
                </div>
                {canEdit && (
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => setCheckFor(current)} title="Contrôle de compréhension" data-testid={`check-lesson-${current.id}`}><ListChecks size={16} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => startEdit(current)} data-testid={`edit-lesson-${current.id}`}><PencilSimple size={16} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(current.id)} data-testid={`del-lesson-${current.id}`}><Trash size={16} /></Button>
                  </div>
                )}
              </div>
              {current.locked ? (
                <div className="mt-5 flex flex-col items-center rounded-lg border border-dashed border-border py-12 text-center" data-testid="lesson-locked">
                  <Lock size={32} weight="fill" className="mb-3 text-accent" />
                  <p className="font-medium">Cette séquence est verrouillée</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {current.lock_reason === "date"
                      ? `Disponible à partir du ${current.available_from}`
                      : "Terminez la séquence précédente (et réussissez son contrôle s'il est obligatoire) pour la débloquer."}
                  </p>
                </div>
              ) : (
                <>
                  {current.video_path ? (
                    <LessonVideoPlayer lesson={current} isStudent={isStudent} />
                  ) : current.video_url && (
                    <a href={current.video_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground hover:bg-accent/90">
                      <VideoCamera size={16} weight="fill" /> Regarder la vidéo
                    </a>
                  )}
                  <ReadingProgressBar targetRef={articleRef} resetKey={current.id} />
                  <SequenceToc containerRef={articleRef} resetKey={current.id} variant="inline" />
                  {isStudent && <LessonHighlights key={current.id} lessonId={current.id} containerRef={articleRef} />}
                  <div className="mt-5 2xl:grid 2xl:grid-cols-[minmax(0,1fr)_210px] 2xl:gap-8">
                    <div ref={articleRef}>
                      <CopyGuard active={isStudent}>
                        <MarkdownContent content={current.content} />
                      </CopyGuard>
                    </div>
                    <aside className="hidden 2xl:block">
                      <SequenceToc containerRef={articleRef} resetKey={current.id} variant="side" />
                    </aside>
                  </div>
                  <div ref={contentEndRef} aria-hidden="true" />
                </>
              )}

              {!current.locked && (
                <div className="mt-6 rounded-lg border border-border p-4">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pièces jointes</p>
                  {(current.attachments || []).length === 0 && !canEdit && (
                    <p className="text-sm text-muted-foreground">Aucune pièce jointe pour cette séquence.</p>
                  )}
                  <div className="space-y-2">
                    {(current.attachments || []).map((att) => (
                      <div key={att.id} className="flex items-center justify-between gap-2 rounded-lg bg-secondary px-3 py-2 text-sm">
                        <button type="button" onClick={() => downloadAttachment(current.id, att)} className="flex min-w-0 items-center gap-2 text-left text-primary hover:underline" data-testid={`lesson-attachment-${att.id}`}>
                          <FileText size={16} className="shrink-0" />
                          <span className="truncate">{att.filename}</span>
                        </button>
                        {canEdit && (
                          <Button size="icon" variant="ghost" onClick={() => deleteAttachment(current.id, att.id)} data-testid={`del-attachment-${att.id}`}>
                            <Trash size={14} className="text-accent" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                  {canEdit && (
                    <div className="mt-3">
                      <Input type="file" accept=".pdf,.doc,.docx,.ppt,.pptx" disabled={uploadingAttachment} onChange={(e) => { uploadAttachment(current.id, e.target.files[0]); e.target.value = ""; }} data-testid="add-lesson-attachment" />
                      {uploadingAttachment && <p className="mt-1 text-xs text-muted-foreground">Envoi en cours...</p>}
                    </div>
                  )}
                </div>
              )}

              {isStudent && !current.locked && current.check_info && (
                <LessonCheck lesson={current} onPassed={() => { load(); loadProgress(); }} />
              )}
              {canEdit && !current.locked && (
                <p className="mt-6 flex flex-wrap items-center gap-2 text-sm text-muted-foreground" data-testid="check-teacher-line">
                  <ListChecks size={16} />
                  {current.check_info
                    ? <>Contrôle de compréhension : {current.check_info.count} question(s){current.check_info.required ? ", obligatoire" : ", facultatif"}, seuil {current.check_info.pass_pct} %.</>
                    : "Pas de contrôle de compréhension pour cette séquence."}
                  <button type="button" className="font-medium text-primary hover:underline" onClick={() => setCheckFor(current)}>{current.check_info ? "Modifier" : "Ajouter"}</button>
                </p>
              )}
              {checkFor && <CheckEditor lesson={checkFor} onClose={() => setCheckFor(null)} onSaved={load} />}

              {isStudent && !current.locked && <LessonNote key={current.id} lessonId={current.id} />}

              {isStudent && !current.locked && (
                <div className="mt-6">
                  <Button
                    onClick={() => toggleDone(current)}
                    disabled={!currentDone && !readingSatisfied}
                    className={currentDone ? "bg-emerald-600 hover:bg-emerald-700" : "bg-primary hover:bg-primary/90"}
                    data-testid="toggle-complete"
                  >
                    <CheckCircle size={16} weight={currentDone ? "fill" : "regular"} className="mr-2" />
                    {currentDone ? "Séquence terminée" : "Marquer comme terminé"}
                  </Button>
                  {!currentDone && !readingSatisfied && (
                    <p className="mt-2 text-xs text-muted-foreground" data-testid="reading-hint">
                      {secondsOnPage < requiredSeconds
                        ? "Continuez la lecture pour pouvoir valider cette séquence..."
                        : "Faites défiler jusqu'à la fin de la séquence pour pouvoir le valider."}
                    </p>
                  )}
                </div>
              )}

              <div className="mt-8 grid gap-3 border-t border-border pt-5 sm:grid-cols-2">
                {(() => {
                  const prev = lessons[active - 1];
                  const next = lessons[active + 1];
                  const modOf = (l) => l?.section_id || null;
                  const modTitle = (l) => sections.find((x) => x.id === l?.section_id)?.title;
                  const strip = (t) => (t || "").replace(/^(Chapitre|Séquence)\s*\d+\s*:\s*/i, "");
                  return (
                    <>
                      <button type="button" disabled={!prev} onClick={() => goTo(active - 1)} data-testid="prev-chapter"
                        className="flex items-center gap-3 rounded-lg border border-border p-3 text-left transition-colors hover:border-primary hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-40">
                        <CaretLeft size={18} className="shrink-0 text-primary" />
                        <span className="min-w-0">
                          <span className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            {prev && modOf(prev) !== modOf(current) ? "Module précédent" : "Précédent"}
                          </span>
                          <span className="line-clamp-2 text-sm font-medium">{prev ? strip(prev.title) : "—"}</span>
                          {prev && modOf(prev) !== modOf(current) && modTitle(prev) && <span className="block truncate text-xs text-muted-foreground">{modTitle(prev)}</span>}
                        </span>
                      </button>
                      <button type="button" disabled={!next} onClick={() => goTo(active + 1)} data-testid="next-chapter"
                        className="flex items-center justify-end gap-3 rounded-lg border border-border p-3 text-right transition-colors hover:border-primary hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-40">
                        <span className="min-w-0">
                          <span className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            {next && modOf(next) !== modOf(current) ? "Module suivant" : "Suivant"}
                          </span>
                          <span className="line-clamp-2 text-sm font-medium">{next ? strip(next.title) : "Fin du cours"}</span>
                          {next && modOf(next) !== modOf(current) && modTitle(next) && <span className="block truncate text-xs text-muted-foreground">{modTitle(next)}</span>}
                        </span>
                        <CaretRight size={18} className="shrink-0 text-primary" />
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
        </>
      )}
    </div>
  );
}

/* ---------------- ASSIGNMENTS ---------------- */
function Assignments({ courseId, canEdit, isStudent }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", due_date: "", max_points: 20, group_mode: false, rubric: [] });
  const [editId, setEditId] = useState(null);
  const [subOpen, setSubOpen] = useState(null);
  const [subText, setSubText] = useState("");
  const [subFile, setSubFile] = useState(null);
  const [subIsNew, setSubIsNew] = useState(true);
  const [gradeOpen, setGradeOpen] = useState(null);
  const [subs, setSubs] = useState([]);

  const load = () => api.get(`/courses/${courseId}/assignments`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [courseId]);

  const resetForm = () => { setForm({ title: "", description: "", due_date: "", max_points: 20, group_mode: false, rubric: [] }); setEditId(null); };
  const startEdit = (a) => { setForm({ title: a.title, description: a.description || "", due_date: a.due_date || "", max_points: a.max_points, group_mode: !!a.group_mode, rubric: a.rubric || [] }); setEditId(a.id); setOpen(true); };
  const save = async () => {
    try {
      if (editId) { await api.put(`/assignments/${editId}`, form); toast.success("Devoir modifié"); }
      else { await api.post(`/courses/${courseId}/assignments`, form); toast.success("Devoir créé"); }
      setOpen(false); resetForm(); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const addCriterion = () => setForm((f) => ({ ...f, rubric: [...f.rubric, { name: "", max_points: 5 }] }));
  const updateCriterion = (i, field, value) => setForm((f) => ({ ...f, rubric: f.rubric.map((c, idx) => (idx === i ? { ...c, [field]: value } : c)) }));
  const removeCriterion = (i) => setForm((f) => ({ ...f, rubric: f.rubric.filter((_, idx) => idx !== i) }));
  const rubricTotal = form.rubric.reduce((sum, c) => sum + (Number(c.max_points) || 0), 0);
  const del = async (a) => {
    if (!window.confirm(`Supprimer le devoir "${a.title}" ? Les copies déjà rendues seront aussi supprimées.`)) return;
    try { await api.delete(`/assignments/${a.id}`); toast.success("Devoir supprimé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const submit = async (aid) => {
    if (subIsNew && !subFile) { toast.error("Merci de joindre un fichier PDF ou Word avant d'envoyer."); return; }
    const fd = new FormData();
    fd.append("content", subText);
    if (subFile) fd.append("file", subFile);
    try {
      await api.post(`/assignments/${aid}/submit`, fd);
      toast.success("Devoir rendu");
      setSubOpen(null); setSubText(""); setSubFile(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const downloadAttachment = async (subId, filename) => {
    try {
      const res = await api.get(`/submissions/${subId}/attachment`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = filename || "piece-jointe";
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  const openGrade = async (a) => { const r = await api.get(`/assignments/${a.id}/submissions`); setSubs(r.data); setGradeOpen(a); };

  return (
    <div className="space-y-4">
      {canEdit && (
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild><Button variant="outline" data-testid="add-assignment-btn"><Plus size={16} className="mr-2" /> Nouveau devoir</Button></DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editId ? "Modifier le devoir" : "Nouveau devoir"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="assignment-title" /></div>
              <div><Label>Consignes</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="assignment-desc" /></div>
              <div className="flex gap-3">
                <div className="flex-1"><Label>Date limite</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
                <div className="w-28">
                  <Label>Points</Label>
                  <Input type="number" value={form.rubric.length > 0 ? rubricTotal : form.max_points} disabled={form.rubric.length > 0} onChange={(e) => setForm({ ...form, max_points: Number(e.target.value) })} />
                </div>
              </div>
              <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-border p-3 text-sm">
                <Checkbox checked={form.group_mode} onCheckedChange={(v) => setForm({ ...form, group_mode: !!v })} data-testid="assignment-group-mode" />
                <span>
                  <span className="font-medium">Devoir de groupe</span>
                  <span className="block text-xs text-muted-foreground">Une seule remise par groupe (parmi les groupes ayant accès à ce cours) — tous les membres voient la même copie et la même note.</span>
                </span>
              </label>
              <div className="rounded-lg border border-border p-3">
                <div className="mb-2 flex items-center justify-between">
                  <div>
                    <Label className="font-medium">Grille de notation (optionnel)</Label>
                    <p className="text-xs text-muted-foreground">Décomposez la note en critères. Le total des points ci-dessus sera calculé automatiquement.</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {form.rubric.map((crit, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input value={crit.name} onChange={(e) => updateCriterion(i, "name", e.target.value)} placeholder="Ex : Fond, Forme, Respect des consignes..." className="flex-1" data-testid={`rubric-name-${i}`} />
                      <Input type="number" value={crit.max_points} onChange={(e) => updateCriterion(i, "max_points", Number(e.target.value))} className="w-20" data-testid={`rubric-points-${i}`} />
                      <Button size="icon" variant="ghost" type="button" onClick={() => removeCriterion(i)}><Trash size={16} className="text-accent" /></Button>
                    </div>
                  ))}
                </div>
                <Button size="sm" variant="outline" type="button" onClick={addCriterion} className="mt-2" data-testid="add-rubric-criterion"><Plus size={14} className="mr-1" /> Ajouter un critère</Button>
                {form.rubric.length > 0 && <p className="mt-2 text-xs font-medium text-primary">Total : {rubricTotal} points</p>}
              </div>
            </div>
            <DialogFooter><Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="save-assignment-btn">{editId ? "Enregistrer" : "Créer"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {items.length === 0 ? (
        <EmptyState
          icon={ClipboardText}
          title="Aucun devoir"
          description={canEdit ? "Créez un devoir pour évaluer vos étudiants sur ce cours." : "Aucun devoir n'a encore été publié pour ce cours."}
          actionLabel={canEdit ? "Nouveau devoir" : undefined}
          onAction={canEdit ? () => setOpen(true) : undefined}
        />
      ) : items.map((a) => (
        <div key={a.id} className="rounded-xl border border-border bg-card p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-display text-lg font-bold">{a.title}</h3>
                {a.group_mode && <Badge variant="secondary"><UsersThree size={12} className="mr-1" /> Devoir de groupe</Badge>}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{a.description}</p>
              <p className="mt-2 text-xs text-muted-foreground">{a.due_date ? `À rendre avant le ${a.due_date}` : "Sans date limite"} · /{a.max_points} points</p>
            </div>
            <div className="flex flex-col items-end gap-2">
              {isStudent && (a.my_submission ? (
                a.my_submission.graded ? (
                  <Badge className="bg-emerald-600 hover:bg-emerald-600">Note : {a.my_submission.grade}/{a.max_points}</Badge>
                ) : (
                  <><Badge variant="secondary"><CheckCircle size={14} className="mr-1" /> Rendu</Badge>
                  <Button size="sm" variant="ghost" onClick={() => { setSubText(a.my_submission.content); setSubFile(null); setSubIsNew(false); setSubOpen(a.id); }}>Modifier</Button></>
                )
              ) : (
                <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={() => { setSubText(""); setSubFile(null); setSubIsNew(true); setSubOpen(a.id); }} data-testid={`submit-${a.id}`}>
                  <PaperPlaneTilt size={14} className="mr-1" /> Rendre
                </Button>
              ))}
              {isStudent && a.my_submission?.attachment_filename && (
                <button type="button" className="text-xs text-primary hover:underline" onClick={() => downloadAttachment(a.my_submission.id, a.my_submission.attachment_filename)}>
                  📎 {a.my_submission.attachment_filename}
                </button>
              )}
              {isStudent && a.my_submission?.group_id && (
                <p className="text-xs text-muted-foreground">Au nom de "{a.my_submission.group_name}" · rendu par {a.my_submission.submitted_by_name}</p>
              )}
              {canEdit && (
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" onClick={() => openGrade(a)} data-testid={`grade-open-${a.id}`}><Eye size={14} className="mr-1" /> Copies ({a.submissions_count || 0}/{a.participants_count ?? "?"})</Button>
                  <Button size="icon" variant="ghost" onClick={() => startEdit(a)} data-testid={`edit-assignment-${a.id}`}><PencilSimple size={16} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(a)} data-testid={`del-assignment-${a.id}`}><Trash size={16} className="text-accent" /></Button>
                </div>
              )}
            </div>
          </div>
          {isStudent && a.my_submission?.graded && a.my_submission.criteria_grades && (
            <div className="mt-3 space-y-1 rounded-lg bg-secondary p-3 text-sm">
              <p className="mb-1 font-semibold">Détail de la note :</p>
              {a.my_submission.criteria_grades.map((cg) => {
                const crit = (a.rubric || []).find((r) => r.name === cg.name);
                return (
                  <div key={cg.name} className="flex items-center justify-between text-xs">
                    <span>{cg.name}</span>
                    <span className="font-medium">{cg.points} / {crit ? crit.max_points : "?"}</span>
                  </div>
                );
              })}
            </div>
          )}
          {isStudent && a.my_submission?.graded && a.my_submission.feedback && (
            <p className="mt-3 rounded-lg bg-secondary p-3 text-sm"><span className="font-semibold">Retour : </span>{a.my_submission.feedback}</p>
          )}
        </div>
      ))}

      {/* submit dialog */}
      <Dialog open={!!subOpen} onOpenChange={(o) => !o && setSubOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rendre le devoir</DialogTitle></DialogHeader>
          <Textarea rows={6} value={subText} onChange={(e) => setSubText(e.target.value)} placeholder="Votre réponse (optionnel, en complément du fichier)..." data-testid="submission-text" />
          <div>
            <Label>Pièce jointe (PDF ou Word) {subIsNew && <span className="text-accent">— obligatoire</span>}</Label>
            <Input type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={(e) => setSubFile(e.target.files[0] || null)} data-testid="submission-file" />
            {!subIsNew && !subFile && <p className="mt-1 text-xs text-muted-foreground">Laissez vide pour garder votre pièce jointe déjà envoyée.</p>}
          </div>
          <DialogFooter><Button onClick={() => submit(subOpen)} className="bg-primary hover:bg-primary/90" data-testid="submit-confirm">Envoyer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* grade dialog */}
      <Dialog open={!!gradeOpen} onOpenChange={(o) => !o && setGradeOpen(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>Copies — {gradeOpen?.title}</DialogTitle></DialogHeader>
          {gradeOpen && (subs.length === 0 ? <EmptyState icon={ClipboardText} title="Aucun étudiant concerné" description="Aucun étudiant n'a accès à ce cours pour l'instant." /> : subs.map((s) => (
            <GradeCard key={s.id || s.student_id || s.group_id} sub={s} max={gradeOpen.max_points} rubric={gradeOpen.rubric} onGraded={() => openGrade(gradeOpen)} onDownload={downloadAttachment} />
          )))}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GradeCard({ sub, max, rubric, onGraded, onDownload }) {
  const hasRubric = rubric && rubric.length > 0;
  const initialCriteria = hasRubric
    ? rubric.map((c) => {
        const existing = (sub.criteria_grades || []).find((cg) => cg.name === c.name);
        return { name: c.name, max_points: c.max_points, points: existing ? existing.points : "" };
      })
    : [];
  const [criteria, setCriteria] = useState(initialCriteria);
  const [grade, setGrade] = useState(sub.grade ?? "");
  const [feedback, setFeedback] = useState(sub.feedback ?? "");

  if (sub.pending) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-dashed border-border p-4 text-muted-foreground">
        <p className="font-medium">{sub.group_id ? `Groupe : ${sub.group_name}` : sub.student_name}</p>
        <Badge variant="secondary">En attente de remise</Badge>
      </div>
    );
  }

  const total = hasRubric ? criteria.reduce((s2, c) => s2 + (Number(c.points) || 0), 0) : Number(grade) || 0;

  const setCriterionPoints = (i, val) => setCriteria((cs) => cs.map((c, idx) => (idx === i ? { ...c, points: val } : c)));

  const save = async () => {
    try {
      if (hasRubric) {
        if (criteria.some((c) => c.points === "" || c.points === null)) { toast.error("Notez chaque critère avant d'enregistrer."); return; }
        await api.post(`/submissions/${sub.id}/grade`, {
          feedback,
          criteria_grades: criteria.map((c) => ({ name: c.name, points: Number(c.points) })),
        });
      } else {
        await api.post(`/submissions/${sub.id}/grade`, { grade: Number(grade), feedback });
      }
      toast.success("Note enregistrée"); onGraded();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-semibold">{sub.group_id ? `Groupe : ${sub.group_name}` : sub.student_name}</p>
          {sub.group_id && <p className="text-xs text-muted-foreground">Rendu par {sub.submitted_by_name}</p>}
        </div>
        {sub.graded && <Badge className="bg-emerald-600 hover:bg-emerald-600">{sub.grade}/{max}</Badge>}
      </div>
      {sub.content && <p className="mt-2 whitespace-pre-wrap rounded bg-secondary p-3 text-sm">{sub.content}</p>}
      {sub.attachment_filename ? (
        <button type="button" className="mt-2 flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm text-primary hover:bg-secondary" onClick={() => onDownload(sub.id, sub.attachment_filename)} data-testid={`download-${sub.id}`}>
          <PaperPlaneTilt size={14} /> Télécharger la pièce jointe ({sub.attachment_filename})
        </button>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">Aucune pièce jointe.</p>
      )}

      {hasRubric ? (
        <div className="mt-3 space-y-2 rounded-lg border border-border p-3">
          <p className="text-xs font-medium text-muted-foreground">Grille de notation</p>
          {criteria.map((c, i) => (
            <div key={c.name} className="flex items-center justify-between gap-3 text-sm">
              <span className="flex-1">{c.name}</span>
              <div className="flex items-center gap-1">
                <Input type="number" min={0} max={c.max_points} value={c.points} onChange={(e) => setCriterionPoints(i, e.target.value)} className="h-8 w-16" data-testid={`rubric-grade-${sub.id}-${i}`} />
                <span className="text-xs text-muted-foreground">/ {c.max_points}</span>
              </div>
            </div>
          ))}
          <p className="border-t border-border pt-2 text-right text-sm font-display font-bold text-primary">Total : {total} / {max}</p>
        </div>
      ) : null}

      <div className="mt-3 flex items-end gap-2">
        {!hasRubric && (
          <div className="w-24"><Label>Note</Label><Input type="number" max={max} value={grade} onChange={(e) => setGrade(e.target.value)} data-testid={`grade-input-${sub.id}`} /></div>
        )}
        <div className="flex-1"><Label>Commentaire</Label><Input value={feedback} onChange={(e) => setFeedback(e.target.value)} /></div>
        <Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid={`grade-save-${sub.id}`}>Noter</Button>
      </div>
    </div>
  );
}

const COVERS = [
  "https://images.pexels.com/photos/10744384/pexels-photo-10744384.jpeg",
  "https://images.pexels.com/photos/31468533/pexels-photo-31468533.jpeg",
  "https://images.pexels.com/photos/33865563/pexels-photo-33865563.jpeg",
];

function DeleteCourse({ course }) {
  const navigate = useNavigate();
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const canConfirm = confirmText.trim() === course.title;

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/courses/${course.id}`);
      toast.success("Cours supprimé");
      navigate("/app/cours");
    } catch (e) { toast.error(apiError(e)); }
    finally { setDeleting(false); }
  };

  return (
    <AlertDialog onOpenChange={(o) => !o && setConfirmText("")}>
      <AlertDialogTrigger asChild>
        <Button variant="outline" className="border-accent text-accent hover:bg-accent/10 hover:text-accent" data-testid="delete-course-btn">
          <Trash size={16} className="mr-2" /> Supprimer le cours
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Supprimer définitivement « {course.title} » ?</AlertDialogTitle>
          <AlertDialogDescription>
            Cette action est irréversible. Toutes les séquences, devoirs (et copies rendues), quiz (et tentatives), messages du forum,
            événements et termes de glossaire liés à ce cours seront supprimés, ainsi que tous les fichiers associés
            (couverture, vidéos, pièces jointes). Les étudiants perdront immédiatement l'accès.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="py-2">
          <Label>Pour confirmer, retapez le titre exact du cours :</Label>
          <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={course.title} className="mt-1" data-testid="delete-course-confirm-input" />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Annuler</AlertDialogCancel>
          <AlertDialogAction
            onClick={doDelete}
            disabled={!canConfirm || deleting}
            className="bg-accent text-accent-foreground hover:bg-accent/90"
            data-testid="confirm-delete-course-btn"
          >
            {deleting ? "Suppression..." : "Supprimer définitivement"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EditCourse({ course, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: course.title, description: course.description || "", code: course.code || "",
    category: course.category || "", cover_image: course.cover_image || "",
  });
  const [uploading, setUploading] = useState(false);
  const uploadCover = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/courses/${course.id}/cover`, fd);
      setForm((f) => ({ ...f, cover_image: data.cover_image }));
      toast.success("Image mise à jour");
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); }
  };
  const save = async () => {
    try {
      await api.put(`/courses/${course.id}`, {
        ...form,
        published: course.published, meeting_url: course.meeting_url, group_ids: course.group_ids || [],
        promotion_ids: course.promotion_ids || [], filiere_ids: course.filiere_ids || [], semestre_ids: course.semestre_ids || [],
      });
      toast.success("Cours modifié");
      setOpen(false); onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="edit-course-btn"><PencilSimple size={16} className="mr-2" /> Modifier le cours</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Modifier le cours</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="edit-course-title" /></div>
          <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="MGT-101" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="edit-course-desc" /></div>
          <div>
            <Label>Image de couverture</Label>
            <div className="mt-2 flex items-center gap-3">
              {coverSrc(form.cover_image) ? (
                <img src={coverSrc(form.cover_image)} alt="Aperçu" className="h-16 w-24 rounded border border-border object-cover" />
              ) : (
                <CourseMonogram code={form.code} title={form.title} className="h-16 w-24 rounded border border-border" />
              )}
              <div className="flex gap-2">
                <button type="button" onClick={() => setForm({ ...form, cover_image: "" })} className={`h-14 w-20 overflow-hidden rounded border-2 ${!form.cover_image ? "border-primary" : "border-transparent"}`} title="Monogramme généré automatiquement">
                  <CourseMonogram code={form.code} title={form.title} className="h-full w-full" />
                </button>
                {COVERS.map((c) => (
                  <button key={c} type="button" onClick={() => setForm({ ...form, cover_image: c })} className={`h-14 w-20 overflow-hidden rounded border-2 ${form.cover_image === c ? "border-primary" : "border-transparent"}`}>
                    <img src={c} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-2">
              <Input type="file" accept=".jpg,.jpeg,.png,.webp,image/*" disabled={uploading} onChange={(e) => uploadCover(e.target.files[0])} data-testid="edit-course-cover-file" />
              {uploading && <p className="mt-1 text-xs text-muted-foreground">Envoi en cours...</p>}
            </div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="save-edit-course-btn">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CourseSettings({ course, onSaved }) {
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState(course.group_ids || []);
  const [academic, setAcademic] = useState([]);
  const [selectedPromotions, setSelectedPromotions] = useState(course.promotion_ids || []);
  const [selectedFilieres, setSelectedFilieres] = useState(course.filiere_ids || []);
  const [selectedSemestres, setSelectedSemestres] = useState(course.semestre_ids || []);
  const [pickFiliere, setPickFiliere] = useState("");
  const [pickPromotion, setPickPromotion] = useState("");
  const [pickWholeFiliere, setPickWholeFiliere] = useState("");
  const [pickSemFiliere, setPickSemFiliere] = useState("");
  const [pickSemPromotion, setPickSemPromotion] = useState("");
  const [pickSemestre, setPickSemestre] = useState("");
  const [meeting, setMeeting] = useState(course.meeting_url || "");
  const [sequential, setSequential] = useState(!!course.sequential_order);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/groups").then((r) => setGroups(r.data)).catch(() => {});
    api.get("/academic").then((r) => setAcademic(r.data)).catch(() => {});
  }, [open]);
  const toggle = (gid) => setSelected((s) => (s.includes(gid) ? s.filter((x) => x !== gid) : [...s, gid]));

  const filieres = academic.filter((a) => a.type === "filiere");
  const filiereObj = filieres.find((f) => f.id === pickFiliere);
  const promotionsForFiliere = academic.filter((a) => a.type === "promotion" && a.parent === pickFiliere);
  const promotionsById = Object.fromEntries(academic.filter((a) => a.type === "promotion").map((p) => [p.id, p]));
  const filiereNameOf = (promo) => academic.find((f) => f.id === promo?.parent)?.name;
  const filieresById = Object.fromEntries(filieres.map((f) => [f.id, f]));
  const semestresById = Object.fromEntries(academic.filter((a) => a.type === "semestre").map((s) => [s.id, s]));
  const promotionsForSemFiliere = academic.filter((a) => a.type === "promotion" && a.parent === pickSemFiliere);
  const semestresForSemPromotion = academic.filter((a) => a.type === "semestre" && a.parent === pickSemPromotion);
  const semestreLabelOf = (sem) => {
    const promo = promotionsById[sem?.parent];
    const fname = filiereNameOf(promo);
    return `${fname || "?"} — ${promo?.name || "?"} — ${sem?.name || "?"}`;
  };

  const addPromotion = () => {
    if (!pickPromotion || selectedPromotions.includes(pickPromotion)) return;
    setSelectedPromotions((s) => [...s, pickPromotion]);
    setPickPromotion("");
  };
  const removePromotion = (id) => setSelectedPromotions((s) => s.filter((x) => x !== id));
  const addWholeFiliere = () => {
    if (!pickWholeFiliere || selectedFilieres.includes(pickWholeFiliere)) return;
    setSelectedFilieres((s) => [...s, pickWholeFiliere]);
    setPickWholeFiliere("");
  };
  const removeWholeFiliere = (id) => setSelectedFilieres((s) => s.filter((x) => x !== id));
  const addSemestre = () => {
    if (!pickSemestre || selectedSemestres.includes(pickSemestre)) return;
    setSelectedSemestres((s) => [...s, pickSemestre]);
    setPickSemestre("");
  };
  const removeSemestre = (id) => setSelectedSemestres((s) => s.filter((x) => x !== id));

  const save = async () => {
    try {
      await api.post(`/courses/${course.id}/groups`, { group_ids: selected });
      await api.post(`/courses/${course.id}/promotions`, { promotion_ids: selectedPromotions });
      await api.post(`/courses/${course.id}/filieres`, { filiere_ids: selectedFilieres });
      await api.post(`/courses/${course.id}/semestres`, { semestre_ids: selectedSemestres });
      await api.put(`/courses/${course.id}`, {
        title: course.title, description: course.description, code: course.code,
        filiere: course.filiere, category: course.category, cover_image: course.cover_image,
        published: course.published, meeting_url: meeting, sequential_order: sequential, group_ids: selected,
        promotion_ids: selectedPromotions, filiere_ids: selectedFilieres, semestre_ids: selectedSemestres,
      });
      toast.success("Paramètres enregistrés");
      setOpen(false); onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="course-settings-btn"><UsersThree size={16} className="mr-2" /> Accès & salle virtuelle</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Accès du cours & salle virtuelle</DialogTitle></DialogHeader>
        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3">
          <input type="checkbox" className="mt-1 h-4 w-4" checked={sequential} onChange={(e) => setSequential(e.target.checked)} data-testid="sequential-order" />
          <span>
            <span className="block text-sm font-semibold">Ordre imposé</span>
            <span className="block text-xs text-muted-foreground">Chaque séquence ne s'ouvre qu'après avoir terminé la précédente (et réussi son contrôle de compréhension s'il est obligatoire). Sinon, les étudiants lisent dans l'ordre qu'ils veulent.</span>
          </span>
        </label>

        <div>
          <Label>Lien de visioconférence</Label>
          <Input value={meeting} onChange={(e) => setMeeting(e.target.value)} placeholder="https://meet.jit.si/..." data-testid="meeting-input" />
        </div>

        <div>
          <Label className="mb-2 block">Filières ayant accès à ce cours (toutes promotions confondues)</Label>
          <p className="mb-2 text-xs text-muted-foreground">Tous les étudiants de la filière choisie auront accès, quelle que soit leur promotion.</p>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Select value={pickWholeFiliere} onValueChange={setPickWholeFiliere}>
                <SelectTrigger data-testid="settings-whole-filiere"><SelectValue placeholder="Choisir une filière..." /></SelectTrigger>
                <SelectContent>
                  {filieres.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button type="button" variant="outline" onClick={addWholeFiliere} disabled={!pickWholeFiliere} data-testid="add-whole-filiere-btn">Ajouter</Button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {selectedFilieres.length === 0 && <p className="text-xs text-muted-foreground">Aucune filière ajoutée.</p>}
            {selectedFilieres.map((id) => (
              <Badge key={id} variant="secondary" className="flex items-center gap-1.5 py-1.5" data-testid={`selected-filiere-${id}`}>
                {filieresById[id]?.name || id}
                <button type="button" onClick={() => removeWholeFiliere(id)} className="text-muted-foreground hover:text-accent"><X size={12} /></button>
              </Badge>
            ))}
          </div>
        </div>

        <div>
          <Label className="mb-2 block">Promotions ayant accès à ce cours</Label>
          <p className="mb-2 text-xs text-muted-foreground">Tous les étudiants de la promotion choisie auront automatiquement accès — pas besoin de les ajouter un par un.</p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[140px]">
              <Label className="text-xs">Filière</Label>
              <Select value={pickFiliere} onValueChange={(v) => { setPickFiliere(v); setPickPromotion(""); }}>
                <SelectTrigger data-testid="settings-filiere"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>
                  {filieres.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[140px]">
              <Label className="text-xs">Promotion</Label>
              <Select value={pickPromotion} onValueChange={setPickPromotion} disabled={!pickFiliere}>
                <SelectTrigger data-testid="settings-promotion"><SelectValue placeholder={pickFiliere ? "Choisir..." : "Filière d'abord"} /></SelectTrigger>
                <SelectContent>
                  {promotionsForFiliere.length === 0 && <SelectItem value="__none" disabled>Aucune promotion</SelectItem>}
                  {promotionsForFiliere.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button type="button" variant="outline" onClick={addPromotion} disabled={!pickPromotion} data-testid="add-promotion-access-btn">Ajouter</Button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {selectedPromotions.length === 0 && <p className="text-xs text-muted-foreground">Aucune promotion ajoutée.</p>}
            {selectedPromotions.map((id) => {
              const promo = promotionsById[id];
              return (
                <Badge key={id} variant="secondary" className="flex items-center gap-1.5 py-1.5" data-testid={`selected-promotion-${id}`}>
                  {promo ? `${filiereNameOf(promo)} — ${promo.name}` : id}
                  <button type="button" onClick={() => removePromotion(id)} className="text-muted-foreground hover:text-accent"><X size={12} /></button>
                </Badge>
              );
            })}
          </div>
        </div>

        <div>
          <Label className="mb-2 block">Semestres ayant accès à ce cours</Label>
          <p className="mb-2 text-xs text-muted-foreground">Le plus précis : seuls les étudiants du semestre exact (même filière et promotion) auront accès.</p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[110px]">
              <Label className="text-xs">Filière</Label>
              <Select value={pickSemFiliere} onValueChange={(v) => { setPickSemFiliere(v); setPickSemPromotion(""); setPickSemestre(""); }}>
                <SelectTrigger data-testid="settings-sem-filiere"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>
                  {filieres.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[110px]">
              <Label className="text-xs">Promotion</Label>
              <Select value={pickSemPromotion} onValueChange={(v) => { setPickSemPromotion(v); setPickSemestre(""); }} disabled={!pickSemFiliere}>
                <SelectTrigger data-testid="settings-sem-promotion"><SelectValue placeholder={pickSemFiliere ? "Choisir..." : "Filière d'abord"} /></SelectTrigger>
                <SelectContent>
                  {promotionsForSemFiliere.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[110px]">
              <Label className="text-xs">Semestre</Label>
              <Select value={pickSemestre} onValueChange={setPickSemestre} disabled={!pickSemPromotion}>
                <SelectTrigger data-testid="settings-semestre"><SelectValue placeholder={pickSemPromotion ? "Choisir..." : "Promotion d'abord"} /></SelectTrigger>
                <SelectContent>
                  {semestresForSemPromotion.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button type="button" variant="outline" onClick={addSemestre} disabled={!pickSemestre} data-testid="add-semestre-access-btn">Ajouter</Button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {selectedSemestres.length === 0 && <p className="text-xs text-muted-foreground">Aucun semestre ajouté.</p>}
            {selectedSemestres.map((id) => (
              <Badge key={id} variant="secondary" className="flex items-center gap-1.5 py-1.5" data-testid={`selected-semestre-${id}`}>
                {semestreLabelOf(semestresById[id])}
                <button type="button" onClick={() => removeSemestre(id)} className="text-muted-foreground hover:text-accent"><X size={12} /></button>
              </Badge>
            ))}
          </div>
        </div>

        <div>
          <Label className="mb-2 block">Groupes ayant accès à ce cours</Label>
          {groups.length === 0 && <p className="text-sm text-muted-foreground">Aucun groupe disponible.</p>}
          <div className="space-y-2">
            {groups.map((g) => (
              <label key={g.id} className="flex cursor-pointer items-center gap-3 rounded-lg border border-border p-3 text-sm" data-testid={`grp-opt-${g.id}`}>
                <Checkbox checked={selected.includes(g.id)} onCheckedChange={() => toggle(g.id)} />
                <span className="font-medium">{g.name}</span>
                <Badge variant="secondary">{g.member_count} étud.</Badge>
              </label>
            ))}
          </div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="save-access-btn">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- QUIZZES ---------------- */
function Quizzes({ courseId, canEdit, isStudent }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [availableFrom, setAvailableFrom] = useState("");
  const [maxAttempts, setMaxAttempts] = useState("");
  const [timeLimitMinutes, setTimeLimitMinutes] = useState("");
  const [questions, setQuestions] = useState([{ question: "", options: ["", "", "", ""], correct_index: 0 }]);
  const [take, setTake] = useState(null);
  const [resultsFor, setResultsFor] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [bankOpen, setBankOpen] = useState(false);
  const [bankItems, setBankItems] = useState([]);
  const [bankSearch, setBankSearch] = useState("");
  const [bankTagFilter, setBankTagFilter] = useState("all");
  const [bankSelected, setBankSelected] = useState([]);

  const load = () => api.get(`/courses/${courseId}/quizzes`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [courseId]);

  const openBank = () => {
    setBankSelected([]);
    api.get("/question-bank").then((r) => setBankItems(r.data)).catch((e) => toast.error(apiError(e)));
    setBankOpen(true);
  };
  const bankTags = [...new Set(bankItems.flatMap((i) => i.tags || []))].sort();
  const filteredBank = bankItems.filter((i) => {
    if (bankTagFilter !== "all" && !(i.tags || []).includes(bankTagFilter)) return false;
    if (bankSearch.trim() && !i.question.toLowerCase().includes(bankSearch.trim().toLowerCase())) return false;
    return true;
  });
  const toggleBankSelect = (id) => setBankSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const importFromBank = () => {
    const toImport = bankItems.filter((i) => bankSelected.includes(i.id)).map((i) => ({ question: i.question, options: [...i.options], correct_index: i.correct_index }));
    setQuestions((qs) => {
      const base = qs.length === 1 && !qs[0].question.trim() ? [] : qs;
      return [...base, ...toImport];
    });
    toast.success(`${toImport.length} question(s) importée(s)`);
    setBankOpen(false);
  };

  const resetForm = () => {
    setTitle(""); setDueDate(""); setAvailableFrom(""); setMaxAttempts(""); setTimeLimitMinutes("");
    setQuestions([{ question: "", options: ["", "", "", ""], correct_index: 0 }]);
    setEditId(null);
  };
  const startEdit = (q) => {
    setTitle(q.title);
    setDueDate(q.due_date || "");
    setAvailableFrom(q.available_from || "");
    setMaxAttempts(q.max_attempts ? String(q.max_attempts) : "");
    setTimeLimitMinutes(q.time_limit_minutes ? String(q.time_limit_minutes) : "");
    setQuestions(q.questions && q.questions.length > 0 ? q.questions.map((qq) => ({ ...qq, correct_index: qq.correct_index ?? 0 })) : [{ question: "", options: ["", "", "", ""], correct_index: 0 }]);
    setEditId(q.id);
    setOpen(true);
  };
  const save = async () => {
    const payload = {
      title, description: "", questions, due_date: dueDate || null, available_from: availableFrom || null,
      max_attempts: maxAttempts ? Number(maxAttempts) : null, time_limit_minutes: timeLimitMinutes ? Number(timeLimitMinutes) : null,
    };
    try {
      if (editId) { await api.put(`/quizzes/${editId}`, payload); toast.success("Quiz modifié"); }
      else { await api.post(`/courses/${courseId}/quizzes`, payload); toast.success("Quiz créé"); }
      setOpen(false); resetForm(); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const startTake = async (q) => {
    try {
      const { data } = await api.post(`/quizzes/${q.id}/start`);
      setTake(q);
      setAnswers(new Array(q.questions.length).fill(-1));
      setResult(null);
      setLeaves(0);
      setTimeLeft(data.time_limit_minutes ? (data.time_left_seconds ?? data.time_limit_minutes * 60) : null);
    } catch (e) { toast.error(apiError(e)); load(); }
  };
  const submitTake = async () => {
    try {
      const { data } = await api.post(`/quizzes/${take.id}/attempt`, { answers });
      setResult(data); setTimeLeft(null);
      if (data.timed_out) toast.error("Temps écoulé : ce quiz est fermé jusqu'à demain."); else toast.success(`Score : ${data.score}/${data.total}`);
      load();
    } catch (e) { toast.error(apiError(e)); setTake(null); setTimeLeft(null); load(); }
  };

  // Pendant un quiz : on compte les sorties de page (autre onglet, autre application)
  const [leaves, setLeaves] = useState(0);
  const lastLeave = useRef(0);
  useEffect(() => {
    if (!take || result || !isStudent) return undefined;
    const onLeave = () => {
      const now = Date.now();
      if (now - lastLeave.current < 1500) return;
      lastLeave.current = now;
      setLeaves((n) => n + 1);
      api.post(`/quizzes/${take.id}/tab-event`).catch(() => {});
    };
    const onVis = () => { if (document.hidden) onLeave(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("blur", onLeave);
    return () => { document.removeEventListener("visibilitychange", onVis); window.removeEventListener("blur", onLeave); };
  }, [take, result, isStudent]);

  useEffect(() => {
    if (timeLeft === null || result) return;
    if (timeLeft <= 0) { toast.error("Temps écoulé, envoi automatique de vos réponses."); submitTake(); return; }
    const t = setTimeout(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, result]);

  return (
    <div className="space-y-4">
      {canEdit && (
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild><Button variant="outline" onClick={resetForm} data-testid="add-quiz-btn"><Plus size={16} className="mr-2" /> Nouveau quiz</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
            <DialogHeader><DialogTitle>{editId ? "Modifier le quiz" : "Créer un quiz"}</DialogTitle></DialogHeader>
            <div><Label>Titre</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="quiz-title" /></div>
            <div><Label>Date limite (optionnel)</Label><Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="quiz-due-date" /></div>
            <div><Label>Disponible à partir du (optionnel)</Label><Input type="date" value={availableFrom} onChange={(e) => setAvailableFrom(e.target.value)} data-testid="quiz-available-from" /></div>
            <div className="flex gap-3">
              <div className="flex-1"><Label>Tentatives maximum (optionnel)</Label><Input type="number" min={1} value={maxAttempts} onChange={(e) => setMaxAttempts(e.target.value)} placeholder="Illimité" data-testid="quiz-max-attempts" /></div>
              <div className="flex-1"><Label>Temps limite en minutes (optionnel)</Label><Input type="number" min={1} value={timeLimitMinutes} onChange={(e) => setTimeLimitMinutes(e.target.value)} placeholder="Sans limite" data-testid="quiz-time-limit" /></div>
            </div>
            {questions.map((q, qi) => (
              <div key={qi} className="rounded-lg border border-border p-3">
                <Input value={q.question} onChange={(e) => { const n = [...questions]; n[qi].question = e.target.value; setQuestions(n); }} placeholder={`Question ${qi + 1}`} className="font-medium" />
                {q.options.map((o, oi) => (
                  <div key={oi} className="mt-2 flex items-center gap-2">
                    <input type="radio" checked={q.correct_index === oi} onChange={() => { const n = [...questions]; n[qi].correct_index = oi; setQuestions(n); }} />
                    <Input value={o} onChange={(e) => { const n = [...questions]; n[qi].options[oi] = e.target.value; setQuestions(n); }} placeholder={`Option ${oi + 1}`} />
                  </div>
                ))}
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              <Button variant="ghost" onClick={() => setQuestions([...questions, { question: "", options: ["", "", "", ""], correct_index: 0 }])}><Plus size={14} className="mr-1" /> Ajouter une question</Button>
              <Button variant="outline" onClick={openBank} data-testid="open-question-bank-btn"><Question size={14} className="mr-1" /> Importer de la banque</Button>
            </div>
            <DialogFooter><Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="save-quiz-btn">{editId ? "Enregistrer les modifications" : "Enregistrer le quiz"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* import from question bank */}
      <Dialog open={bankOpen} onOpenChange={setBankOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>Importer de la banque de questions</DialogTitle></DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <Input className="flex-1 min-w-[160px]" placeholder="Rechercher..." value={bankSearch} onChange={(e) => setBankSearch(e.target.value)} data-testid="bank-picker-search" />
            {bankTags.length > 0 && (
              <Select value={bankTagFilter} onValueChange={setBankTagFilter}>
                <SelectTrigger className="w-44" data-testid="bank-picker-tag"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les thèmes</SelectItem>
                  {bankTags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{bankSelected.length} sélectionnée(s)</p>
          <div className="max-h-80 space-y-2 overflow-y-auto">
            {filteredBank.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Aucune question dans la banque. Ajoutez-en depuis la page "Banque de questions".</p>}
            {filteredBank.map((i) => (
              <label key={i.id} className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 text-sm hover:bg-secondary/50">
                <Checkbox checked={bankSelected.includes(i.id)} onCheckedChange={() => toggleBankSelect(i.id)} />
                <div>
                  <p className="font-medium">{i.question}</p>
                  <div className="mt-1 flex flex-wrap gap-1">{(i.tags || []).map((t) => <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>)}</div>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter><Button onClick={importFromBank} disabled={bankSelected.length === 0} className="bg-primary hover:bg-primary/90" data-testid="confirm-import-bank-btn">Importer ({bankSelected.length})</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {items.length === 0 ? (
        <EmptyState
          icon={Question}
          title="Aucun quiz"
          description={canEdit ? "Créez un quiz pour tester les connaissances de vos étudiants." : "Aucun quiz n'a encore été publié pour ce cours."}
          actionLabel={canEdit ? "Nouveau quiz" : undefined}
          onAction={canEdit ? () => setOpen(true) : undefined}
        />
      ) : items.map((q) => (
        <div key={q.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-5">
          <div>
            <h3 className="font-display text-lg font-bold">{q.title}</h3>
            <p className="text-sm text-muted-foreground">
              {q.questions?.length || 0} questions
              {q.max_attempts && ` · ${q.max_attempts} tentative(s) max`}
              {q.time_limit_minutes && ` · ${q.time_limit_minutes} min chrono`}
            </p>
            {isStudent && q.locked && (
              <p className="mt-1 flex items-center gap-1 text-xs text-accent"><Lock size={14} weight="fill" /> Disponible à partir du {q.available_from}</p>
            )}
            {isStudent && !q.locked && q.max_attempts && (
              <p className="mt-1 text-xs text-muted-foreground">Tentatives : {q.attempts_used}/{q.max_attempts}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isStudent && q.my_attempt && <Badge className="bg-emerald-600 hover:bg-emerald-600">{q.my_attempt.score}/{q.my_attempt.total}</Badge>}
            {isStudent && (
              q.locked ? (
                <Button size="sm" variant="outline" disabled data-testid={`take-quiz-${q.id}`}><Lock size={14} weight="fill" className="mr-2 text-accent" /> Verrouillé</Button>
              ) : q.locked_until ? (
                <Button size="sm" variant="outline" disabled data-testid={`take-quiz-${q.id}`}><Clock size={14} className="mr-2" /> Fermé jusqu'à demain</Button>
              ) : q.attempts_exhausted ? (
                <Button size="sm" variant="outline" disabled data-testid={`take-quiz-${q.id}`}>Tentatives épuisées</Button>
              ) : (
                <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={() => startTake(q)} data-testid={`take-quiz-${q.id}`}>{q.my_attempt ? "Refaire" : "Passer"}</Button>
              )
            )}
            {canEdit && <Button size="icon" variant="ghost" onClick={() => setResultsFor(q)} title="Résultats" data-testid={`quiz-results-${q.id}`}><ChartBar size={16} /></Button>}
            {canEdit && <Button size="icon" variant="ghost" onClick={() => startEdit(q)} data-testid={`edit-quiz-${q.id}`}><PencilSimple size={16} /></Button>}
            {canEdit && <Button size="icon" variant="ghost" onClick={async () => { await api.delete(`/quizzes/${q.id}`); load(); }}><Trash size={16} /></Button>}
          </div>
        </div>
      ))}

      {resultsFor && <QuizResults quiz={resultsFor} onClose={() => setResultsFor(null)} />}

      {/* take quiz */}
      <Dialog open={!!take} onOpenChange={(o) => !o && setTake(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3">
              <span>{take?.title}</span>
              {timeLeft !== null && !result && (
                <span className={`flex items-center gap-1 rounded-full px-3 py-1 text-sm font-mono ${timeLeft <= 30 ? "bg-accent/10 text-accent" : "bg-secondary text-foreground"}`} data-testid="quiz-timer">
                  <Clock size={16} /> {String(Math.floor(timeLeft / 60)).padStart(2, "0")}:{String(timeLeft % 60).padStart(2, "0")}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {take?.time_limit_minutes && !result && (
            <p className="rounded-lg bg-muted/60 p-3 text-xs text-muted-foreground" data-testid="quiz-strict-note">
              Chrono strict : si le temps est écoulé, vos réponses sont envoyées automatiquement et le quiz est fermé jusqu'à demain. Fermer cette fenêtre ne met pas le chrono en pause.
            </p>
          )}
          {leaves > 0 && !result && (
            <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200" data-testid="quiz-leaves">
              Vous avez quitté la page {leaves} fois pendant ce quiz. Votre professeur en est informé.
            </p>
          )}
          <CopyGuard active noSelect className="space-y-3">
          {take?.questions.map((q, qi) => (
            <div key={qi} className="rounded-lg border border-border p-4">
              <p className="font-medium">{qi + 1}. {q.question}</p>
              <div className="mt-2 space-y-2">
                {q.options.map((o, oi) => {
                  const correct = result && result.corrections[qi].correct_index === oi;
                  const chosen = answers[qi] === oi;
                  return (
                    <label key={oi} className={`flex cursor-pointer items-center gap-2 rounded-lg border p-2 text-sm ${result ? (correct ? "border-emerald-500 bg-emerald-50" : chosen ? "border-accent bg-red-50" : "border-border") : chosen ? "border-primary bg-primary/5" : "border-border"}`}>
                      <input type="radio" disabled={!!result} checked={chosen} onChange={() => { const n = [...answers]; n[qi] = oi; setAnswers(n); }} data-testid={`quiz-q${qi}-o${oi}`} />
                      {o}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
          </CopyGuard>
          {result ? (
            <div className="rounded-lg bg-primary p-4 text-center text-primary-foreground">
              <p className="font-display text-2xl font-extrabold">{result.score}/{result.total}</p>
              <p className="text-sm">Résultat du quiz</p>
            </div>
          ) : (
            <DialogFooter><Button onClick={submitTake} className="bg-primary hover:bg-primary/90" data-testid="quiz-submit">Valider mes réponses</Button></DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- FORUM ---------------- */
function Forum({ courseId }) {
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const [replyOpen, setReplyOpen] = useState(null);
  const [replyText, setReplyText] = useState("");

  const load = () => api.get(`/courses/${courseId}/forum`).then((r) => setPosts(r.data));
  useEffect(() => { load(); }, [courseId]);

  const post = async () => {
    if (!content.trim()) return;
    try { await api.post(`/courses/${courseId}/forum`, { title, content }); setContent(""); setTitle(""); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const reply = async (topicId) => {
    if (!replyText.trim()) return;
    try {
      await api.post(`/courses/${courseId}/forum`, { content: replyText, parent_id: topicId });
      setReplyText(""); setReplyOpen(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const topics = posts.filter((p) => !p.parent_id);
  const repliesOf = (topicId) => posts.filter((p) => p.parent_id === topicId);

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-5">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Sujet (optionnel)" className="mb-2" data-testid="forum-title" />
        <Textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Écrivez votre message..." data-testid="forum-input" />
        <Button onClick={post} className="mt-3 bg-primary hover:bg-primary/90" data-testid="forum-post-btn"><PaperPlaneTilt size={16} className="mr-2" /> Publier</Button>
      </div>
      {topics.length === 0 ? <EmptyState icon={ChatsCircle} title="Aucune discussion" description="Soyez le premier à lancer un sujet dans ce forum." /> : topics.map((p) => (
        <div key={p.id} className="rounded-xl border border-border bg-card p-5" data-testid={`forum-topic-${p.id}`}>
          <ForumAuthor p={p} />
          {p.title && <p className="mt-2 font-display font-bold">{p.title}</p>}
          <p className="mt-1 whitespace-pre-wrap text-sm">{p.content}</p>

          {repliesOf(p.id).length > 0 && (
            <div className="mt-4 ml-4 space-y-3 border-l-2 border-border pl-4">
              {repliesOf(p.id).map((r) => (
                <div key={r.id} data-testid={`forum-reply-${r.id}`}>
                  <ForumAuthor p={r} small />
                  <p className="mt-1 whitespace-pre-wrap text-sm">{r.content}</p>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4">
            {replyOpen === p.id ? (
              <div className="flex items-start gap-2">
                <Textarea rows={2} value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Votre réponse..." className="flex-1" data-testid={`forum-reply-input-${p.id}`} />
                <div className="flex flex-col gap-1">
                  <Button size="sm" onClick={() => reply(p.id)} className="bg-primary hover:bg-primary/90" data-testid={`forum-reply-send-${p.id}`}>Envoyer</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setReplyOpen(null); setReplyText(""); }}>Annuler</Button>
                </div>
              </div>
            ) : (
              <button type="button" className="flex items-center gap-1 text-xs font-medium text-primary hover:underline" onClick={() => { setReplyOpen(p.id); setReplyText(""); }} data-testid={`forum-reply-btn-${p.id}`}>
                <ChatsCircle size={14} /> Répondre {repliesOf(p.id).length > 0 && `(${repliesOf(p.id).length})`}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function LessonVideoPlayer({ lesson, isStudent }) {
  const videoRef = useRef(null);
  const lastSentRef = useRef(0);

  const sendProgress = () => {
    const v = videoRef.current;
    if (!v || !isStudent || !v.duration || Number.isNaN(v.duration)) return;
    api.post(`/lessons/${lesson.id}/watch-progress`, { seconds_watched: v.currentTime, duration: v.duration }).catch(() => {});
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.currentTime - lastSentRef.current >= 10) {
      lastSentRef.current = v.currentTime;
      sendProgress();
    }
  };

  return (
    <div className="mt-3">
      <video
        ref={videoRef}
        controls
        className="aspect-video w-full rounded-lg bg-black"
        src={lessonVideoUrl(lesson.id)}
        onTimeUpdate={isStudent ? handleTimeUpdate : undefined}
        onPause={isStudent ? sendProgress : undefined}
        onEnded={isStudent ? sendProgress : undefined}
        data-testid={`video-player-${lesson.id}`}
      >
        {lesson.subtitle_path && <track kind="subtitles" srcLang="fr" label="Français" src={lessonSubtitleUrl(lesson.id)} default />}
      </video>
      {isStudent && lesson.my_watch_percent > 0 && (
        <p className="mt-1 text-xs text-muted-foreground">Visionné à {lesson.my_watch_percent}% {lesson.my_watch_percent >= 90 && "· séquence marquée terminée automatiquement"}</p>
      )}
    </div>
  );
}

function ForumAuthor({ p, small }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`flex items-center justify-center rounded-full bg-primary font-bold text-primary-foreground ${small ? "h-6 w-6 text-[10px]" : "h-8 w-8 text-xs"}`}>{p.author_name[0]}</div>
      <div>
        <div className="text-sm font-semibold">{p.author_name} <Badge variant="secondary" className="ml-1 text-[10px]">{p.author_role}</Badge></div>
        <p className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleString("fr-FR")}</p>
      </div>
    </div>
  );
}


